// src/views/admin/company/CompanyPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Input } from '@/components/ui/inputs/input';
import { InputTextarea } from '@/components/ui/inputs/input-textarea';
import { InputSelect } from '@/components/ui/inputs/input-select';
import ToastMe from '@/components/ui/ToastMe';
import PermissionCheck from '@/components/common/PermissionCheck';
import { fetchCompanies, createCompany, updateCompany } from '@/services/CompanyService';
import { getSettingService, updateSettingService } from '@/services/SettingServices';

const FRONTEND_GATEWAY_KEYS = {
  waafi: 'frontend_show_waafi',
  edahab: 'frontend_show_edahab',
  premier: 'frontend_show_premier_wallets',
};

const STATUS_OPTIONS = [
  { value: 1, label: 'Active' },
  { value: 2, label: 'Inactive' },
  { value: 3, label: 'Archived' },
];

const COUNTRY_CODE_OPTIONS = [
  { value: 'SOM', label: 'Somalia (SOM)' },
  { value: 'IND', label: 'India (IND)' },
];

const FALLBACK_TZS = [
  'Africa/Mogadishu','Africa/Nairobi','Africa/Addis_Ababa','Africa/Johannesburg',
  'Africa/Cairo','Africa/Lagos','Africa/Algiers',
  'Asia/Dubai','Asia/Karachi','Asia/Kolkata','Asia/Dhaka','Asia/Bangkok',
  'Asia/Singapore','Asia/Hong_Kong','Asia/Tokyo','Asia/Shanghai','Asia/Seoul',
  'Europe/London','Europe/Berlin','Europe/Paris','Europe/Madrid','Europe/Rome',
  'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
  'America/Toronto','America/Mexico_City','America/Sao_Paulo',
  'Australia/Sydney','Pacific/Auckland'
];

function buildTimezoneOptions() {
  try {
    const tzs = Intl.supportedValuesOf?.('timeZone') || [];
    return tzs.sort().map((z) => ({ value: z, label: z }));
  } catch {
    return FALLBACK_TZS.sort().map((z) => ({ value: z, label: z }));
  }
}
const TIMEZONE_OPTIONS = buildTimezoneOptions();

export default function CompanyPage({ pageTitle = 'Company' }) {
  const dispatch = useDispatch();
  const {
    control, handleSubmit, reset,
  } = useForm();

  const [company, setCompany]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState('');
  const [gatewayVisibility, setGatewayVisibility] = useState({
    waafi: '1',
    edahab: '1',
    premier: '1',
  });

  const defaultValues = useMemo(() => ({
    name: '', code: '', timezone: '', currency: '', status: 1,
    email: '', phone: '', website: '', city: '', state: '',
    country_code: '', zip_code: '', address: '',
  }), []);

  // Load the (single) company
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetchCompanies();
        const rows = res?.data?.data || [];
        const one  = rows[0] || null;
        setCompany(one);
        // Persist the name locally so headers can use it immediately
        try { if (one?.name) localStorage.setItem('company_name', one.name); } catch {}
        reset(one ? {
          name: one.name || '',
          code: one.code || '',
          timezone: one.timezone || '',
          currency: one.currency || '',
          status: one.status ?? 1,
          email: one.email || '',
          phone: one.phone || '',
          website: one.website || '',
          city: one.city || '',
          state: one.state || '',
          country_code: one.country_code || '',
          zip_code: one.zip_code || '',
          address: one.address || '',
        } : defaultValues);
      } catch (e) {
        ToastMe(e?.message || 'Failed to load company', 'error');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(getSettingService({ key_list: Object.values(FRONTEND_GATEWAY_KEYS) }))
      .then((res) => {
        const data = res?.data?.data || {};
        setGatewayVisibility({
          waafi: String(data[FRONTEND_GATEWAY_KEYS.waafi] ?? '1'),
          edahab: String(data[FRONTEND_GATEWAY_KEYS.edahab] ?? '1'),
          premier: String(data[FRONTEND_GATEWAY_KEYS.premier] ?? '1'),
        });
      })
      .catch(() => {});
  }, [dispatch]);

  const toggleGatewayVisibility = async (key) => {
    const current = String(gatewayVisibility[key] ?? '1');
    const next = current === '1' ? '0' : '1';
    const settingKey = FRONTEND_GATEWAY_KEYS[key];
    if (!settingKey) return;

    setVisibilitySaving(key);
    try {
      await dispatch(updateSettingService({ [settingKey]: next }));
      setGatewayVisibility((prev) => ({ ...prev, [key]: next }));
      ToastMe(next === '1' ? 'Gateway shown on frontend' : 'Gateway hidden on frontend', 'success');
    } catch (e) {
      ToastMe('Failed to update gateway visibility', 'error');
    } finally {
      setVisibilitySaving('');
    }
  };

  const onSubmit = async (form) => {
    if (loading) return;
    try {
      setSaving(true);

      // normalize selects
      if (typeof form.status === 'object' && form.status?.value != null) form.status = form.status.value;
      if (typeof form.country_code === 'object' && form.country_code?.value) form.country_code = form.country_code.value;
      if (typeof form.timezone === 'object' && form.timezone?.value) form.timezone = form.timezone.value;

      // never send empty code (it’s required in schema)
      if (typeof form.code === 'string' && form.code.trim() === '') delete form.code;

      if (company && company._id) {
        const res = await updateCompany(company._id, form);
        const updated = res?.data?.data;
        setCompany(updated);
        try { if (updated?.name) localStorage.setItem('company_name', updated.name); } catch {}
        reset({ ...updated });
        ToastMe('Company updated', 'success');
      } else {
        // create (only when none exists)
        try {
          const res = await createCompany({
            ...form,
            createDefaultBranch: true,
            defaultBranchName: 'Main',
          });
          const created = res?.data?.data?.company;
          setCompany(created);
          try { if (created?.name) localStorage.setItem('company_name', created.name); } catch {}
          reset({ ...created });
          ToastMe('Company created', 'success');
        } catch (e) {
          // if server says it already exists, switch to edit mode seamlessly
          if (e?.response?.status === 409) {
            const existing = e?.response?.data?.data || null;
            if (existing) {
              setCompany(existing);
              try { if (existing?.name) localStorage.setItem('company_name', existing.name); } catch {}
              reset({
                name: existing.name || '',
                code: existing.code || '',
                timezone: existing.timezone || '',
                currency: existing.currency || '',
                status: existing.status ?? 1,
                email: existing.email || '',
                phone: existing.phone || '',
                website: existing.website || '',
                city: existing.city || '',
                state: existing.state || '',
                country_code: existing.country_code || '',
                zip_code: existing.zip_code || '',
                address: existing.address || '',
              });
              ToastMe('A company already exists. Switched to edit mode.', 'info');
            } else {
              // fallback refetch
              const re = await fetchCompanies();
              const rows = re?.data?.data || [];
              const one  = rows[0] || null;
              if (one) {
                setCompany(one);
                try { if (one?.name) localStorage.setItem('company_name', one.name); } catch {}
                reset({
                  name: one.name || '',
                  code: one.code || '',
                  timezone: one.timezone || '',
                  currency: one.currency || '',
                  status: one.status ?? 1,
                  email: one.email || '',
                  phone: one.phone || '',
                  website: one.website || '',
                  city: one.city || '',
                  state: one.state || '',
                  country_code: one.country_code || '',
                  zip_code: one.zip_code || '',
                  address: one.address || '',
                });
                ToastMe('A company already exists. Switched to edit mode.', 'info');
              }
            }
          } else {
            const msg = e?.response?.data?.message || e?.message || 'Save failed';
            ToastMe(msg, 'error');
          }
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="db-card db-tab-div active">
      <div className="db-card-header">
        <h3 className="db-card-title">
          {pageTitle} {company?.name ? <span className="text-gray-400">({company.name})</span> : null}
        </h3>
      </div>

      <div className="db-card-body">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <Controller
              name="name"
              control={control}
              rules={{ required: true, minLength: 1 }}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title required" size="sm"
                       labelText="Company Name" placeholder="e.g., TabanFood" />
              )}
            />

            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title" size="sm"
                       labelText="Code (unique)" placeholder="e.g., TABAN (leave blank to keep/auto)" />
              )}
            />

            <Controller
              name="timezone"
              control={control}
              render={({ field }) => {
                const val =
                  typeof field.value === 'object'
                    ? field.value
                    : (field.value && TIMEZONE_OPTIONS.find(o => o.value === field.value)) || null;
                return (
                  <InputSelect
                    {...field}
                    value={val}
                    onChange={(opt) => field.onChange(opt)}
                    options={TIMEZONE_OPTIONS}
                    labelStyleClassName="db-field-title"
                    labelText="Timezone"
                    placeholder="e.g., Africa/Mogadishu"
                  />
                );
              }}
            />

            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title" size="sm"
                       labelText="Currency" placeholder="e.g., SOS" />
              )}
            />

            <Controller
              name="status"
              control={control}
              render={({ field }) => {
                const val =
                  typeof field.value === 'object'
                    ? field.value
                    : STATUS_OPTIONS.find(o => o.value === Number(field.value)) || STATUS_OPTIONS[0];
                return (
                  <InputSelect
                    {...field}
                    value={val}
                    onChange={(opt) => field.onChange(opt)}
                    options={STATUS_OPTIONS}
                    labelStyleClassName="db-field-title"
                    labelText="Status"
                  />
                );
              }}
            />

            <Controller name="email" control={control}
              rules={{ pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/ }}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Email" type="email" />
              )}
            />

            <Controller name="phone" control={control}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Phone" />
              )}
            />

            <Controller name="website" control={control}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Website" placeholder="https://…" />
              )}
            />

            <Controller name="city" control={control}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="City" />
              )}
            />

            <Controller name="state" control={control}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="State" />
              )}
            />

            <Controller
              name="country_code"
              control={control}
              render={({ field }) => {
                const val =
                  typeof field.value === 'object'
                    ? field.value
                    : COUNTRY_CODE_OPTIONS.find(o => o.value === field.value) || null;
                return (
                  <InputSelect
                    {...field}
                    value={val}
                    onChange={(opt) => field.onChange(opt)}
                    options={COUNTRY_CODE_OPTIONS}
                    labelStyleClassName="db-field-title"
                    labelText="Country Code"
                    placeholder="Select Country Code"
                  />
                );
              }}
            />

            <Controller name="zip_code" control={control}
              render={({ field }) => (
                <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Zip Code" />
              )}
            />

            <Controller name="address" control={control}
              render={({ field }) => (
                <InputTextarea {...field} rows={4} groupClassName="sm:col-span-2"
                  className="h-auto" labelStyleClassName="db-field-title" size="sm" labelText="Address" />
              )}
            />

            <PermissionCheck permsission="update_settings">
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  className="db-btn text-white bg-primary"
                  disabled={saving || loading}
                >
                  <i className="lab lab-save"></i>
                  <span>{company ? 'Save Changes' : 'Create Company'}</span>
                  {(saving || loading) && <i className="fa-solid fa-spinner fa-spin ml-2" />}
                </button>
              </div>
            </PermissionCheck>
          </div>
        </form>

        <div className="mt-8 border-t border-gray-100 pt-6">
          <h4 className="text-lg font-medium mb-2">Frontend Gateway Visibility</h4>
          <p className="text-sm text-gray-500 mb-4">
            Use these buttons to hide the old checkout gateways while Raams is active.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => toggleGatewayVisibility('waafi')}
              disabled={visibilitySaving === 'waafi'}
              className={`db-btn ${gatewayVisibility.waafi === '1' ? 'bg-green-600 text-white' : 'bg-red-500 text-white'}`}
            >
              {gatewayVisibility.waafi === '1' ? 'Hide Waafi' : 'Show Waafi'}
            </button>
            <button
              type="button"
              onClick={() => toggleGatewayVisibility('edahab')}
              disabled={visibilitySaving === 'edahab'}
              className={`db-btn ${gatewayVisibility.edahab === '1' ? 'bg-green-600 text-white' : 'bg-red-500 text-white'}`}
            >
              {gatewayVisibility.edahab === '1' ? 'Hide Edahab' : 'Show Edahab'}
            </button>
            <button
              type="button"
              onClick={() => toggleGatewayVisibility('premier')}
              disabled={visibilitySaving === 'premier'}
              className={`db-btn ${gatewayVisibility.premier === '1' ? 'bg-green-600 text-white' : 'bg-red-500 text-white'}`}
            >
              {gatewayVisibility.premier === '1' ? 'Hide Premier' : 'Show Premier'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
